import { Component, inject, Input, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ConfigDsdAdresseService } from '@services/config-dsd-adresse.service';
import { FormulaireCoordonneesMouvementAdresseModel } from 'src/app/models/formulaire/formulaire-coordonnees-mouvement-adresse.model';

interface AdresseReadyEvent {
  detail: {
    name: string;
    elements: Record<string, HTMLDsdInputElement | HTMLDsdSelectElement | HTMLDsdComboboxElement>;
  };
}

interface DateDebutUtilisationEvent {
  detail?: {
    value?: string;
  };
}

@Component({
  selector: 'app-modification-adresse',
  templateUrl: './modification-adresse.component.html',
  standalone: false,
})
export class ModificationAdresseComponent implements OnInit {
  @Input() formGroup!: FormGroup<FormulaireCoordonneesMouvementAdresseModel>;

  adresseCityRef!: HTMLDsdInputElement;
  adresseProvinceRef!: HTMLDsdSelectElement;
  adresseCodePostalRef!: HTMLDsdInputElement;
  adresseStreetRef!: HTMLDsdInputElement;
  adresseUnitRef!: HTMLDsdInputElement;

  dateMin: string = '';
  dateMax: string = '';
  champsInitialises: boolean = false;
  configAdresse: unknown;

  private readonly configDsdAdresseService = inject(ConfigDsdAdresseService);

  ngOnInit(): void {
    this.definirPlageDate();
    this.configAdresse = this.configDsdAdresseService.getConfigAdresseMouvement();
  }

  public onAddressReady($eventAdresse: AdresseReadyEvent): void {
    this.setAdresseValidation($eventAdresse, true);
  }

  public setAdresseValidation($eventAdresse: AdresseReadyEvent, firstInit: boolean = false): void {
    const { name } = $eventAdresse.detail;

    this.adresseCityRef = $eventAdresse.detail.elements[name + '-city'] as HTMLDsdInputElement;
    this.adresseProvinceRef = $eventAdresse.detail.elements[
      name + '-province'
    ] as HTMLDsdSelectElement;
    this.adresseCodePostalRef = $eventAdresse.detail.elements[name + '-pc'] as HTMLDsdInputElement;
    this.adresseStreetRef = $eventAdresse.detail.elements[name + '-street'] as HTMLDsdInputElement;
    this.adresseUnitRef = $eventAdresse.detail.elements[name + '-unit'] as HTMLDsdInputElement;

    // Pré-remplir les inputs avec la valeur du formGroup à l'initialisation seulement
    if (firstInit) {
      this.adresseStreetRef.value = this.formGroup.get('numeroCiviqueNomRue')?.value ?? '';
      this.adresseCityRef.value = this.formGroup.get('ville')?.value ?? '';
      this.adresseUnitRef.value = this.formGroup.get('appartement')?.value ?? '';
      this.adresseCodePostalRef.value = this.formGroup.get('codePostal')?.value ?? '';
      this.adresseProvinceRef.value = this.formGroup.get('province')?.value ?? '';
    }

    this.hookControlToEventChange(this.formGroup.get('numeroCiviqueNomRue'), this.adresseStreetRef);
    this.hookControlToEventChange(this.formGroup.get('ville'), this.adresseCityRef);
    this.hookControlToEventChange(this.formGroup.get('appartement'), this.adresseUnitRef);
    this.hookControlToEventChange(this.formGroup.get('codePostal'), this.adresseCodePostalRef);
    this.hookControlToEventChange(this.formGroup.get('province'), this.adresseProvinceRef);

    this.setValidationsPourChampsAdresse();
  }

  private setValidationsPourChampsAdresse() {
    this.adresseStreetRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'numeroCiviqueNomRue', 'textevide');
      },
      { errorKey: 'textevide' },
    );
    this.adresseStreetRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'numeroCiviqueNomRue', 'separation');
      },
      { errorKey: 'separation' },
    );
    this.adresseStreetRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'numeroCiviqueNomRue', 'authorizedCharacters');
      },
      { errorKey: 'authorizedCharacters' },
    );

    this.adresseUnitRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'appartement', 'appartement');
      },
      { errorKey: 'appartement' },
    );

    this.adresseCityRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'ville', 'textevide');
      },
      { errorKey: 'textevide' },
    );
    this.adresseCityRef.registerCustomValidate(
      (value) => {
        return this.validerInput(value, 'ville', 'authorizedCharacters');
      },
      { errorKey: 'authorizedCharacters' },
    );
  }

  validerInput(value: string, nomFormControl: string, codeErreur: string) {
    const control = this.formGroup.get(nomFormControl);
    if (!control) return false;
    control.setValue(value);
    return !control.hasError(codeErreur);
  }

  hookControlToEventChange(
    control: AbstractControl | null,
    input: HTMLDsdInputElement | HTMLDsdSelectElement | HTMLDsdComboboxElement,
  ): void {
    const eventName = this.detectEventName(input);
    if (control && input) {
      input.addEventListener(eventName, () => {
        control.setValue(input.value);
        // Pour gérer le dsd combobox qui retourne un array
        const val: string = Array.isArray(input.value) ? input.value[0] : input.value;
        control?.setValue(val);
      });
    }
  }

  public onAddressSelected(event: AdresseReadyEvent): void {
    // Vérifie que l'événement contient bien la propriété 'elements' dans 'detail'.
    // Si ce n'est pas le cas, on quitte la méthode
    if (!event?.detail?.elements) {
      return;
    }
    if (this.adresseContientPlusieursChamps(event)) {
      this.setAdresseValidation(event);
      this.champsInitialises = true;
    }

    const elements = event.detail?.elements;
    const name = event.detail?.name;

    if (elements && name) {
      this.formGroup.patchValue({
        numeroCiviqueNomRue: elements[name + '-street']?.value.toString() ?? '',
        appartement: elements[name + '-unit']?.value.toString() ?? '',
        ville: elements[name + '-city']?.value.toString() ?? '',
        province: elements[name + '-province']?.value.toString() ?? '',
        codePostal: elements[name + '-pc']?.value.toString() ?? '',
        codePays: 'CA',
      });
    }
  }

  public onDateDebutUtilisation(event: DateDebutUtilisationEvent): void {
    const date = event.detail?.value ?? '';
    this.formGroup.patchValue({ dateDebutUtilisation: date });
  }

  private adresseContientPlusieursChamps(event: AdresseReadyEvent): boolean {
    return !this.champsInitialises && Object.keys(event.detail.elements).length > 1;
  }

  private definirPlageDate(): void {
    const aujourdhui = new Date();
    this.dateMin = aujourdhui.toISOString().split('T')[0];
    const dansUnAn = new Date(aujourdhui);
    dansUnAn.setFullYear(aujourdhui.getFullYear() + 1);
    this.dateMax = dansUnAn.toISOString().split('T')[0];
  }

  detectEventName(element: HTMLElement): string {
    if (!element?.nodeName) {
      return 'change';
    }

    const nodeName = element.nodeName.toUpperCase();

    switch (nodeName) {
      case 'DSD-INPUT':
        return 'dsdInputChange';
      case 'DSD-SELECT':
        return 'dsdSelectChange';
      default:
        return 'change';
    }
  }
}
